import React, { useState, useEffect } from 'react';
import api from '../services/api';
import NumbersList from './NumbersList';

// ── SVG icon set ────────────────────────────────────────────────────────────
const EyeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1.5 10S4.5 4 10 4s8.5 6 8.5 6-3 6-8.5 6S1.5 10 1.5 10z" strokeLinejoin="round"/>
    <circle cx="10" cy="10" r="2.5"/>
  </svg>
);
const EyeOffIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 3l14 14M8.5 8.6A2.5 2.5 0 0 0 12.4 12.4M6.1 6.2C3.9 7.5 2 10 2 10s2.5 5 8 5a9 9 0 0 0 3.9-.9M10 5c5.5 0 8 5 8 5a13.5 13.5 0 0 1-1.9 2.8" strokeLinejoin="round" strokeLinecap="round"/>
  </svg>
);
const UserIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="7" r="3"/>
    <path d="M3 18c0-3.9 3.1-7 7-7s7 3.1 7 7" strokeLinecap="round"/>
  </svg>
);
const PhoneIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 2h4l2 5-2.5 1.5a11 11 0 0 0 4 4L13 10l5 2v4a2 2 0 0 1-2 2C6.5 18 2 13.5 2 4a2 2 0 0 1 2-2z" strokeLinejoin="round"/>
  </svg>
);
const KeyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="7.5" cy="11.5" r="3.5"/>
    <path d="M10.5 9.5l7-7M15.5 3l1.5 1.5M13 5.5l1.5 1.5" strokeLinecap="round"/>
  </svg>
);
const LinkIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M8.5 11.5a4 4 0 0 0 5.66 0l2-2a4 4 0 0 0-5.66-5.66l-1 1" strokeLinecap="round"/>
    <path d="M11.5 8.5a4 4 0 0 0-5.66 0l-2 2a4 4 0 0 0 5.66 5.66l1-1" strokeLinecap="round"/>
  </svg>
);
const PlusIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 4v12M4 10h12" strokeLinecap="round"/>
  </svg>
);
const EditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M13.5 3.5l3 3-10 10H3.5v-3l10-10z" strokeLinejoin="round"/>
  </svg>
);
const TrashIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 5h14M8 5V3h4v2M6 5l1 12h6l1-12" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CheckIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 10l4 4 8-8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round"/>
  </svg>
);

const BellIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 2a6 6 0 0 1 6 6c0 3.5 1 5 1 5H3s1-1.5 1-5a6 6 0 0 1 6-6z" strokeLinejoin="round"/>
    <path d="M8.3 17a1.7 1.7 0 0 0 3.4 0" strokeLinecap="round"/>
  </svg>
);

export default function Settings({ theme = 'dark' }) {
  const [apiKey, setApiKey] = useState('');
  const [carrierStatus, setCarrierStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Contacts state
  const [contacts, setContacts]               = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newContactName, setNewContactName]   = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [contactSaving, setContactSaving]     = useState(false);
  const [contactMsg, setContactMsg]           = useState('');
  const [editingId, setEditingId]             = useState(null);
  const [editName, setEditName]               = useState('');
  const [editPhone, setEditPhone]             = useState('');

  // Ring volume (persisted in localStorage, read by Dashboard on each ring)
  const [ringVolume, setRingVolume] = useState(
    () => parseFloat(localStorage.getItem('pbx_ring_volume') ?? '0.5')
  );
  const handleRingVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setRingVolume(v);
    localStorage.setItem('pbx_ring_volume', String(v));
  };

  const playPreviewBeep = (vol) => {
    if (vol === 0) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      [0, 0.18, 0.36].forEach(offset => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 440;
        gain.gain.setValueAtTime(vol, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.12);
      });
      // close context after beeps finish
      setTimeout(() => { try { ctx.close(); } catch (_) {} }, 800);
    } catch (_) {}
  };

  // Slack state
  const [slackStatus, setSlackStatus]               = useState(null);
  const [slackChannels, setSlackChannels]           = useState([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  const [slackDisconnecting, setSlackDisconnecting] = useState(false);
  const [slackMsg, setSlackMsg]                     = useState('');

  useEffect(() => {
    fetchCarrierStatus();
    fetchSlackStatus();
    fetchContacts();

    const params = new URLSearchParams(window.location.search);
    const slackParam = params.get('slack');
    if (slackParam === 'connected') {
      setSlackMsg('Slack connected. Pick a channel below to start seeing messages.');
      window.history.replaceState({}, '', '/dashboard');
      fetchSlackStatus();
    } else if (slackParam === 'error') {
      const reason = params.get('reason') || 'unknown';
      setSlackMsg(`Slack connection failed (${reason}). Try again.`);
      window.history.replaceState({}, '', '/dashboard');
    } else if (slackParam === 'cancelled') {
      setSlackMsg('Slack connection cancelled.');
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  const fetchCarrierStatus = async () => {
    try {
      const response = await api.get('/carrier/status');
      setCarrierStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch carrier status:', err);
    }
  };

  const fetchSlackStatus = async () => {
    try {
      const res = await api.get('/slack/status');
      setSlackStatus(res.data);
      if (res.data.connected && !res.data.channelId) fetchSlackChannels();
    } catch {
      setSlackStatus({ connected: false });
    }
  };

  const fetchSlackChannels = async () => {
    setSlackChannelsLoading(true);
    try {
      const res = await api.get('/slack/channels');
      setSlackChannels(res.data);
    } catch {
      setSlackChannels([]);
    } finally {
      setSlackChannelsLoading(false);
    }
  };

  const fetchContacts = async () => {
    setContactsLoading(true);
    try { const res = await api.get('/contacts'); setContacts(res.data || []); } catch (_) {}
    finally { setContactsLoading(false); }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContactName.trim() || !newContactPhone.trim()) return;
    setContactSaving(true);
    try {
      await api.post('/contacts', { name: newContactName.trim(), phoneNumber: newContactPhone.trim() });
      setNewContactName(''); setNewContactPhone('');
      setContactMsg('Contact saved.');
      await fetchContacts();
      setTimeout(() => setContactMsg(''), 2500);
    } catch { setContactMsg('Could not save contact.'); }
    finally { setContactSaving(false); }
  };

  const handleDeleteContact = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch { setContactMsg('Could not delete contact.'); }
  };

  const startEdit  = (c) => { setEditingId(c.id); setEditName(c.name); setEditPhone(c.phoneNumber); };
  const cancelEdit = ()  => { setEditingId(null); setEditName(''); setEditPhone(''); };
  const handleSaveEdit = async (id) => {
    if (!editName.trim() || !editPhone.trim()) return;
    try {
      await api.put(`/contacts/${id}`, { name: editName.trim(), phoneNumber: editPhone.trim() });
      setContacts(prev => prev.map(c => c.id === id ? { ...c, name: editName.trim(), phoneNumber: editPhone.trim() } : c));
      cancelEdit();
    } catch { setContactMsg('Could not update contact.'); }
  };

  const handleSlackConnect = async () => {
    try {
      const res = await api.get('/slack/auth-url');
      window.location.href = res.data.url;
    } catch { setSlackMsg('Could not reach the server. Try again.'); }
  };

  const handleSlackChannelSelect = async (e) => {
    const channelId = e.target.value;
    const channel = slackChannels.find((c) => c.id === channelId);
    if (!channel) return;
    try {
      await api.post('/slack/channel', { channelId: channel.id, channelName: channel.name });
      setSlackStatus((prev) => ({ ...prev, channelId: channel.id, channelName: channel.name }));
      setSlackMsg(`Channel #${channel.name} selected. Messages will appear in the Softphone panel.`);
    } catch { setSlackMsg('Could not save channel selection. Try again.'); }
  };

  const handleSlackDisconnect = async () => {
    setSlackDisconnecting(true);
    try {
      await api.delete('/slack/disconnect');
      setSlackStatus({ connected: false });
      setSlackChannels([]);
      setSlackMsg('Slack disconnected.');
    } catch { setSlackMsg('Could not disconnect. Try again.'); }
    finally { setSlackDisconnecting(false); }
  };

  const handleConnectCarrier = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!apiKey.trim()) { setError('Please enter your Telnyx API key'); return; }
    setLoading(true);
    try {
      const response = await api.post('/carrier/connect', { apiKey });
      setSuccess(`Successfully connected! Found ${response.data.numbers?.length || 0} phone numbers.`);
      setApiKey('');
      fetchCarrierStatus();
    } catch (err) {
      setError('Failed to connect carrier: ' + (err.response?.data?.message || err.message));
    } finally { setLoading(false); }
  };

  // ── Theme tokens ────────────────────────────────────────────────────────────
  const dark    = theme === 'dark';
  const text    = dark ? 'text-white'          : 'text-gray-900';
  const muted   = dark ? 'text-white/60'       : 'text-gray-600';
  const faint   = dark ? 'text-white/35'       : 'text-gray-400';
  const surface = dark ? 'bg-white/5'          : 'bg-gray-50';
  const border  = dark ? 'border-white/10'     : 'border-gray-200';
  const input   = dark
    ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:ring-white/20 focus:border-white/25'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-400';

  // ── Reusable sub-components ─────────────────────────────────────────────────
  const Card = ({ children, className = '' }) => (
    <div className={`rounded-2xl border p-5 ${surface} ${border} ${className}`}>{children}</div>
  );

  const SectionHead = ({ icon: Icon, label }) => (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className={`h-3.5 w-3.5 ${faint}`} />}
      <p className={`text-xs font-semibold uppercase tracking-wider ${faint}`}>{label}</p>
    </div>
  );

  const isErr = (msg) => /could not|fail|error|cancel/i.test(msg);
  const Alert = ({ msg, onDismiss }) => {
    if (!msg) return null;
    const err = isErr(msg);
    return (
      <div className={`flex items-start justify-between gap-3 rounded-xl px-4 py-2.5 mb-4 text-sm border ${
        err
          ? dark ? 'bg-red-500/10 border-red-500/20 text-red-300'   : 'bg-red-50 border-red-300 text-red-700'
          : dark ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-green-50 border-green-300 text-green-700'
      }`}>
        <span>{msg}</span>
        {onDismiss && (
          <button onClick={onDismiss} className="shrink-0 opacity-50 hover:opacity-100 transition mt-0.5">
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Notifications & Sound ── */}
      <Card>
        <SectionHead icon={BellIcon} label="Notifications & Sound" />

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`text-xs font-medium ${faint}`}>Ring Volume</label>
            <span className={`text-xs font-semibold tabular-nums ${muted}`}>
              {ringVolume === 0
                ? 'Off'
                : ringVolume <= 0.25
                ? 'Low'
                : ringVolume <= 0.5
                ? 'Medium'
                : ringVolume <= 0.75
                ? 'High'
                : 'Max'}
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={ringVolume}
            onChange={handleRingVolumeChange}
            onPointerUp={(e) => playPreviewBeep(parseFloat(e.target.value))}
            onKeyUp={(e)     => playPreviewBeep(parseFloat(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${
                ringVolume === 0 ? '#6b7280' : '#38bdf8'
              } ${ringVolume * 100}%, rgba(255,255,255,0.1) ${ringVolume * 100}%)`,
            }}
          />

          <div className={`flex justify-between text-xs mt-1.5 ${faint} select-none`}>
            <span>Off</span>
            <span>Low</span>
            <span>Medium</span>
            <span>High</span>
            <span>Max</span>
          </div>
        </div>
      </Card>

      {/* ── Contacts ── */}
      <Card>
        <SectionHead icon={UserIcon} label="Contacts" />
        <Alert msg={contactMsg} onDismiss={() => setContactMsg('')} />

        {/* Add contact form */}
        <form onSubmit={handleAddContact} className="flex gap-2 mb-4">
          <input
            value={newContactName}
            onChange={e => setNewContactName(e.target.value)}
            placeholder="Name"
            className={`flex-1 px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-1 transition ${input}`}
          />
          <input
            value={newContactPhone}
            onChange={e => setNewContactPhone(e.target.value)}
            placeholder="+1 555 000 0000"
            className={`flex-1 px-3 py-2 border rounded-xl text-sm font-mono focus:outline-none focus:ring-1 transition ${input}`}
          />
          <button
            type="submit"
            disabled={contactSaving || !newContactName.trim() || !newContactPhone.trim()}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-40 ${
              dark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add
          </button>
        </form>

        {/* Contact list */}
        {contactsLoading ? (
          <p className={`text-sm ${faint}`}>Loading…</p>
        ) : contacts.length === 0 ? (
          <p className={`text-sm ${faint}`}>No contacts yet. Add one above.</p>
        ) : (
          <div className="space-y-2">
            {contacts.map(c => (
              <div key={c.id} className={`rounded-xl px-3 py-2.5 flex items-center gap-3 ${dark ? 'bg-white/5' : 'bg-white border border-gray-100'}`}>
                {editingId === c.id ? (
                  <>
                    <input value={editName} onChange={e => setEditName(e.target.value)}
                      className={`flex-1 px-2 py-1 rounded-lg text-sm border focus:outline-none ${input}`} />
                    <input value={editPhone} onChange={e => setEditPhone(e.target.value)}
                      className={`flex-1 px-2 py-1 rounded-lg text-sm border font-mono focus:outline-none ${input}`} />
                    <button onClick={() => handleSaveEdit(c.id)}
                      className={`p-1.5 rounded-lg transition ${dark ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-emerald-600 hover:bg-emerald-50'}`}
                      title="Save">
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEdit}
                      className={`p-1.5 rounded-lg transition ${dark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`}
                      title="Cancel">
                      <XIcon className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${text}`}>{c.name}</p>
                      <p className={`text-xs font-mono truncate ${faint}`}>{c.phoneNumber}</p>
                    </div>
                    <button onClick={() => startEdit(c)}
                      className={`p-1.5 rounded-lg transition ${dark ? 'text-white/25 hover:text-white/70 hover:bg-white/8' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                      title="Edit">
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDeleteContact(c.id)}
                      className={`p-1.5 rounded-lg transition ${dark ? 'text-white/25 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                      title="Delete">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Phone Numbers ── */}
      <Card>
        <SectionHead icon={PhoneIcon} label="Phone Numbers" />
        <NumbersList theme={theme} />
      </Card>

      {/* ── Carrier Status ── */}
      <Card>
        <SectionHead label="Carrier Status" />
        {carrierStatus ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-xs mb-1 ${faint}`}>Connection</p>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${carrierStatus.status === 'active' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <p className={`text-sm font-medium ${text}`}>
                  {carrierStatus.status === 'active' ? 'Connected' : 'Not connected'}
                </p>
              </div>
            </div>
            <div>
              <p className={`text-xs mb-1 ${faint}`}>Phone Numbers</p>
              <p className={`text-sm font-medium ${text}`}>{carrierStatus.numberCount}</p>
            </div>
            {carrierStatus.sipConnectionId && (
              <div className="col-span-2">
                <p className={`text-xs mb-1 ${faint}`}>SIP Connection ID</p>
                <p className={`text-xs font-mono ${muted}`}>{carrierStatus.sipConnectionId}</p>
              </div>
            )}
          </div>
        ) : (
          <p className={`text-sm ${faint}`}>No carrier connected yet.</p>
        )}
      </Card>

      {/* ── Connect Telnyx ── */}
      <Card>
        <SectionHead icon={KeyIcon} label="Connect Telnyx Account" />
        <Alert msg={error}   onDismiss={() => setError('')} />
        <Alert msg={success} onDismiss={() => setSuccess('')} />

        <form onSubmit={handleConnectCarrier} className="space-y-4">
          <div>
            <label className={`block text-xs font-medium mb-2 ${faint}`}>Telnyx API Key</label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className={`w-full px-4 py-2 pr-10 border rounded-xl focus:ring-1 focus:outline-none font-mono text-sm transition ${input}`}
                placeholder="KEY…"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition ${faint} hover:opacity-100`}
              >
                {showApiKey
                  ? <EyeOffIcon className="h-4 w-4" />
                  : <EyeIcon    className="h-4 w-4" />}
              </button>
            </div>
            <p className={`text-xs mt-2 ${faint}`}>
              Get your key from{' '}
              <a href="https://portal.telnyx.com" target="_blank" rel="noopener noreferrer"
                className={`underline ${dark ? 'hover:text-white' : 'text-blue-600 hover:text-blue-800'}`}>
                portal.telnyx.com
              </a>
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-xl text-sm font-semibold transition disabled:opacity-50 ${
              dark
                ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/20'
                : 'bg-sky-600 hover:bg-sky-700 text-white'
            }`}
          >
            {loading ? 'Connecting…' : 'Connect Telnyx Account'}
          </button>
        </form>
      </Card>

      {/* ── Slack Integration ── */}
      <Card>
        <SectionHead label="Slack" />
        <Alert msg={slackMsg} onDismiss={() => setSlackMsg('')} />

        {slackStatus === null ? (
          <p className={`text-sm ${faint}`}>Checking Slack status…</p>
        ) : !slackStatus.connected ? (
          <div>
            <p className={`text-sm mb-4 ${muted}`}>
              Connect your Slack workspace to read messages from any channel inside the softphone panel.
            </p>
            <button
              onClick={handleSlackConnect}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                dark
                  ? 'bg-white/8 hover:bg-white/15 text-white border border-white/10'
                  : 'bg-[#4A154B] hover:bg-[#3e1140] text-white'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 54 54" fill="none">
                <path d="M19.7 33.5a4.3 4.3 0 1 1-4.3-4.3h4.3v4.3z" fill="#E01E5A"/>
                <path d="M21.9 33.5a4.3 4.3 0 0 1 8.6 0v10.7a4.3 4.3 0 1 1-8.6 0V33.5z" fill="#E01E5A"/>
                <path d="M26.2 19.7a4.3 4.3 0 1 1 4.3-4.3v4.3h-4.3z" fill="#36C5F0"/>
                <path d="M26.2 21.9a4.3 4.3 0 0 1 0 8.6H15.5a4.3 4.3 0 1 1 0-8.6h10.7z" fill="#36C5F0"/>
                <path d="M40 26.2a4.3 4.3 0 1 1 4.3 4.3H40v-4.3z" fill="#2EB67D"/>
                <path d="M37.8 26.2a4.3 4.3 0 0 1-8.6 0V15.5a4.3 4.3 0 1 1 8.6 0v10.7z" fill="#2EB67D"/>
                <path d="M33.5 40a4.3 4.3 0 1 1-4.3 4.3V40h4.3z" fill="#ECB22E"/>
                <path d="M33.5 37.8a4.3 4.3 0 0 1 0-8.6h10.7a4.3 4.3 0 1 1 0 8.6H33.5z" fill="#ECB22E"/>
              </svg>
              Connect Slack
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Workspace info */}
            <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${dark ? 'bg-white/5' : 'bg-white border border-gray-100'}`}>
              <div>
                <p className={`text-xs mb-0.5 ${faint}`}>Connected workspace</p>
                <p className={`text-sm font-semibold ${text}`}>{slackStatus.teamName}</p>
                {slackStatus.channelId && (
                  <p className={`text-xs mt-0.5 ${faint}`}>Channel: <span className={muted}>#{slackStatus.channelName}</span></p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className={`text-xs font-medium ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>Connected</span>
              </div>
            </div>

            {/* Channel selector */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${faint}`}>
                {slackStatus.channelId ? 'Change channel' : 'Pick a channel to display'}
              </label>
              {slackChannels.length === 0 && !slackChannelsLoading ? (
                <button onClick={fetchSlackChannels} className={`text-xs underline ${faint} hover:opacity-100`}>
                  Load channels
                </button>
              ) : slackChannelsLoading ? (
                <p className={`text-xs ${faint}`}>Loading channels…</p>
              ) : (
                <select
                  defaultValue={slackStatus.channelId || ''}
                  onChange={handleSlackChannelSelect}
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${input}`}
                >
                  <option value="" disabled>Select a channel…</option>
                  {slackChannels.map((ch) => (
                    <option key={ch.id} value={ch.id}>#{ch.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Disconnect */}
            <button
              onClick={handleSlackDisconnect}
              disabled={slackDisconnecting}
              className={`text-xs transition disabled:opacity-50 ${dark ? 'text-white/30 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
            >
              {slackDisconnecting ? 'Disconnecting…' : 'Disconnect Slack'}
            </button>
          </div>
        )}
      </Card>

      {/* ── Quick Links ── */}
      <Card>
        <SectionHead icon={LinkIcon} label="Quick Links" />
        <ul className="space-y-2">
          {[
            { label: 'Telnyx API Documentation', href: 'https://telnyx.com/docs' },
            { label: 'Telnyx Portal',             href: 'https://portal.telnyx.com' },
          ].map(({ label, href }) => (
            <li key={href}>
              <a href={href} target="_blank" rel="noopener noreferrer"
                className={`text-sm transition ${dark ? 'text-white/50 hover:text-white' : 'text-blue-600 hover:underline'}`}>
                {label} ↗
              </a>
            </li>
          ))}
        </ul>
      </Card>

    </div>
  );
}
